import { track } from "./analytics";

/**
 * Client-side error tracking for the SPA (issue #18, acceptance criterion #2).
 *
 * Uncaught errors, unhandled promise rejections, and render crashes caught by
 * the ErrorBoundary are reported to GA4 as the standard `exception` event
 * (description + fatal), landing in the same property as the funnel events —
 * no new dependency, no new data processor.
 *
 * PII-safe by construction (criterion #3): only a scrubbed one-line description
 * is sent — never form values, and never a raw stack (stack frames can carry
 * user-entered data). `scrubPii` redacts emails, phone numbers, and long digit
 * runs, then caps length. Resilient like the rest of analytics: never throws,
 * dedups, and caps total volume so a crash loop can't flood the property.
 */

/** GA4 caps `exception.description` at 100 chars; keep some headroom, still bounded. */
const MAX_DESCRIPTION = 300;

/** Ceiling on distinct reports per page load — a crash loop must not flood GA4. */
const MAX_REPORTS = 25;

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
// A run that looks like a phone number or long id: 7+ digits, possibly split by
// spaces, dots, dashes, parens, or a leading +. Short numbers (line numbers,
// small counts) are left alone.
const LONG_NUMBER = /[+(]?\d(?:[\d\s().-]{5,})\d/g;

/** Redact anything that could be PII, then bound the length. */
export function scrubPii(text: string): string {
  return text
    .replace(EMAIL, "[redacted]")
    .replace(LONG_NUMBER, "[redacted]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_DESCRIPTION);
}

/** Emit signature shared with analytics.track; injectable for tests. */
type Emit = (event: string, params: Record<string, string | number | boolean>) => void;

export interface ReportOptions {
  /** Did this crash take down the UI (error boundary) vs. a survivable error? */
  fatal?: boolean;
  /** Where the error came from: "error-boundary" | "window.error" | "unhandledrejection". */
  source?: string;
}

/** Per-page-load state, so dedup/cap reset with each fresh load (and between tests). */
let seen = new Set<string>();
let reportCount = 0;

/** Test-only: clear the dedup set and volume counter. */
export function resetErrorReporting(): void {
  seen = new Set<string>();
  reportCount = 0;
}

/** Normalize any thrown value into a scrubbed `Name: message` description. */
function describe(error: unknown): string {
  let raw: string;
  if (error instanceof Error) {
    raw = error.name ? `${error.name}: ${error.message}` : error.message;
  } else if (typeof error === "string") {
    raw = error;
  } else {
    try {
      raw = String(error);
    } catch {
      raw = "unknown error";
    }
  }
  return scrubPii(raw) || "unknown error";
}

/**
 * Report a client-side error as a GA4 `exception` event. Never throws; dedups
 * identical descriptions and caps total volume for the page load. `emit`
 * defaults to analytics.track (itself a no-op when analytics is unavailable).
 */
export function reportError(error: unknown, opts: ReportOptions = {}, emit: Emit = track): void {
  try {
    const description = describe(error);
    if (seen.has(description) || reportCount >= MAX_REPORTS) return;
    seen.add(description);
    reportCount++;
    emit("exception", {
      description,
      fatal: opts.fatal ?? false,
      error_source: opts.source ?? "unknown",
    });
  } catch {
    /* error reporting must never break the app */
  }
}

/**
 * Wire global handlers for uncaught errors and unhandled promise rejections.
 * Called once at startup (main.tsx). `target`/`emit` are injectable for tests.
 */
export function installGlobalErrorHandlers(
  target: EventTarget = window,
  emit: Emit = track,
): void {
  target.addEventListener("error", (event) => {
    const e = event as ErrorEvent;
    reportError(e.error ?? e.message ?? "Unknown error", { source: "window.error" }, emit);
  });
  target.addEventListener("unhandledrejection", (event) => {
    const e = event as PromiseRejectionEvent;
    reportError(e.reason ?? "Unhandled rejection", { source: "unhandledrejection" }, emit);
  });
}
